import type { JSX } from 'react';
import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Input, Modal, Popconfirm, Skeleton, message } from 'antd';
import {
  CheckCircleFilled,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  FieldTimeOutlined,
  FolderAddOutlined,
  HistoryOutlined,
  HolderOutlined,
  MoreOutlined,
  PlusOutlined,
  RightOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';

import { authApi } from '../lib/authApi';
import { standardsApi, type StandardGroupDto, type StandardsResponseDto } from '../lib/wikiApi';
import { kbAckColor, kbApi, kbShortDate, type KbStandardItem } from '../lib/api/kb';
import { VersionHistory } from '../components/VersionHistory';
import { NewArticleModal } from '../components/NewArticleModal';
import { Button, Chip, PageTitle, Ring, Tag, plural, type TagColor } from '../components/ds';
import './KnowledgeBase.css';

type StandardItemDto = KbStandardItem;

/**
 * Стандарты сети (HUB 2.0, прототип «standards»). Шапка: сотруднику —
 * кольцо «Подтверждено X из Y», УК — средний % подтверждений по сети.
 * Ниже — чипы категорий и группы-блоки (дерево с вложенными папками) со
 * строками «название · версия · обновлён · статус · действие»: УК видит
 * полоску «Подтвердили по сети N %» (networkAckPct, только kb:admin),
 * остальные — «Подтвердить» (открывает стандарт, подтверждение там) или
 * «Подтверждено». Drag за ⠿ (kb:admin): реордер и перенос между блоками,
 * ⋮ блока — переименовать / подблок / удалить пустой.
 * Стандарт = статья с requiresAck.
 */

// ─── Статус строки ────────────────────────────────────────────────────
type StatusKind = 'acked' | 'pending' | 'newversion';
const STATUS_META: Record<StatusKind, { color: TagColor; label: string }> = {
  acked: { color: 'green', label: 'Ознакомлен' },
  pending: { color: 'gold', label: 'Ждёт подтверждения' },
  newversion: { color: 'blue', label: 'Новая редакция' },
};
/** Статус строки: подтверждён → acked, иначе новая версия (есть ack другой версии) / pending. */
function statusOf(s: StandardItemDto): StatusKind {
  if (s.isAcked) return 'acked';
  if (s.ackedVersion != null && s.ackedVersion < s.version) return 'newversion';
  return 'pending';
}

/** Виртуальный id «Без блока» — нераспределённые стандарты. */
const UNGROUPED = '__ungrouped__';

interface GroupNode {
  group: StandardGroupDto | null; // null = «Без блока»
  children: GroupNode[];
  items: StandardItemDto[]; // стандарты, привязанные напрямую к этой группе
}

export function StandardsPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  // kbApi.standards — тот же /wiki/standards, но с networkAckPct для kb:admin.
  const { data, isLoading, error } = useQuery({
    queryKey: ['wiki', 'standards'],
    queryFn: () => kbApi.standards(),
    retry: false,
  });
  const meQuery = useQuery({ queryKey: ['wiki', 'me'], queryFn: () => authApi.me(), retry: false });
  const canEdit = meQuery.data?.canCreate ?? false;
  // kb:admin — управление таксономией групп и сетевые проценты.
  const canAdmin = meQuery.data?.canAdmin ?? false;

  const [cat, setCat] = useState<string>('Все');
  const [historyId, setHistoryId] = useState<string | null>(null);
  // «Создать стандарт» — та же модалка, что «Создать статью», с типом «Стандарт» (16.10.2026: раньше вела
  // на несуществующий /article/new — «Статья «new» не найдена»).
  const [newStandardOpen, setNewStandardOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropGroup, setDropGroup] = useState<string | null>(null);
  const [groupModal, setGroupModal] = useState<
    | { mode: 'create'; parentId: string | null; parentName: string | null }
    | { mode: 'rename'; group: StandardGroupDto }
    | null
  >(null);

  const items = data?.items ?? [];
  const groups = data?.groups ?? [];
  const visibleItems = useMemo(() => (cat === 'Все' ? items : items.filter((i) => i.category === cat)), [items, cat]);
  const tree = useMemo<GroupNode[]>(() => buildTree(groups, visibleItems), [groups, visibleItems]);

  // ── Drag: перенос/реордер стандарта (оптимистично + откат) ──────────
  const assignMutation = useMutation({
    mutationFn: (vars: { id: string; groupId: string | null; sortOrder?: number }) =>
      standardsApi.assignGroup(vars.id, { groupId: vars.groupId, sortOrder: vars.sortOrder }),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ['wiki', 'standards'] });
      const prev = qc.getQueryData<StandardsResponseDto>(['wiki', 'standards']);
      if (prev) {
        qc.setQueryData<StandardsResponseDto>(['wiki', 'standards'], {
          ...prev,
          items: prev.items.map((i) =>
            i.id === vars.id ? { ...i, standardGroupId: vars.groupId, sortOrder: vars.sortOrder ?? i.sortOrder } : i,
          ),
        });
      }
      return { prev };
    },
    onError: (e: Error, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['wiki', 'standards'], ctx.prev);
      message.error(e.message || 'Не удалось перенести стандарт');
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['wiki', 'standards'] });
    },
  });

  /** Дроп стандарта на блок/папку: в конец целевой группы. */
  function handleDropOnGroup(groupId: string | null): void {
    const id = dragId;
    setDragId(null);
    setDropGroup(null);
    if (!id) return;
    const dragged = items.find((i) => i.id === id);
    if (!dragged) return;
    if ((dragged.standardGroupId ?? null) === groupId) return;
    const inTarget = items.filter((i) => (i.standardGroupId ?? null) === groupId);
    const maxOrder = inTarget.reduce((m, i) => Math.max(m, i.sortOrder), 0);
    assignMutation.mutate({ id, groupId, sortOrder: maxOrder + 10 });
  }

  /** Дроп стандарта на другой стандарт: встать перед ним (и перенестись в его группу). */
  function handleDropOnItem(target: StandardItemDto): void {
    const id = dragId;
    setDragId(null);
    setDropGroup(null);
    if (!id || id === target.id) return;
    if (!items.some((i) => i.id === id)) return;
    assignMutation.mutate({ id, groupId: target.standardGroupId ?? null, sortOrder: target.sortOrder - 1 });
  }

  function toggle(id: string): void {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (error) return <Alert type="error" showIcon message="Не удалось загрузить стандарты" description={(error as Error).message} />;

  const stats = data?.stats ?? { total: 0, acknowledged: 0, pending: 0, overdue: 0, percent: 0 };
  const netValues = items.map((i) => i.networkAckPct).filter((v): v is number => typeof v === 'number');
  const netAvg = netValues.length ? Math.round(netValues.reduce((a, b) => a + b, 0) / netValues.length) : null;
  const sub = canAdmin
    ? `${stats.total} ${plural(stats.total, 'стандарт', 'стандарта', 'стандартов')}${netAvg != null ? ` · в среднем по сети подтвердили ${netAvg} %` : ''}`
    : `${stats.total} ${plural(stats.total, 'стандарт', 'стандарта', 'стандартов')} сети · подтверждайте новые редакции вовремя`;

  return (
    <div className="fk-kb-stack" style={{ gap: 20 }}>
      <PageTitle
        title="Стандарты"
        sub={sub}
        style={{ marginBottom: 0 }}
        right={
          <>
            {!canAdmin && stats.total > 0 && (
              <div className="fk-kb-panel fk-kb-std-progress">
                <Ring value={stats.percent} size={48} stroke={5} doneColor="var(--fk-success)" />
                <div style={{ lineHeight: 1.25 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>
                    Подтверждено {stats.acknowledged} из {stats.total}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--fk-text-muted)' }}>
                    {stats.pending ? `Осталось ${stats.pending}${stats.overdue ? ` · просрочено ${stats.overdue}` : ''}` : 'Все подтверждены'}
                  </div>
                </div>
              </div>
            )}
            {canEdit && (
              <Button variant="primary" icon={<PlusOutlined />} onClick={() => setNewStandardOpen(true)}>
                Создать стандарт
              </Button>
            )}
          </>
        }
      />

      {canEdit && <NewArticleModal open={newStandardOpen} onClose={() => setNewStandardOpen(false)} defaultType="standard" />}

      {(data?.categories ?? []).length > 1 && (
        <div className="fk-kb-chips">
          {['Все', ...(data?.categories ?? [])].map((c) => (
            <Chip key={c} active={cat === c} onClick={() => setCat(c)}>
              {c}
            </Chip>
          ))}
        </div>
      )}

      {canAdmin && (
        <div className="fk-kb-muted" style={{ fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 6, marginTop: -8 }}>
          <HolderOutlined /> Перетащите стандарт за ручку, чтобы изменить порядок или перенести в другой блок
        </div>
      )}

      {tree.length === 0 ? (
        <div className="fk-kb-panel fk-kb-empty fk-kb-muted">Нет стандартов в этой категории</div>
      ) : (
        tree.map((node) => (
          <GroupBlock
            key={node.group?.id ?? UNGROUPED}
            node={node}
            depth={0}
            collapsed={collapsed}
            onToggle={toggle}
            navigate={navigate}
            canAdmin={canAdmin}
            canEdit={canEdit}
            dragId={dragId}
            dropGroup={dropGroup}
            setDragId={setDragId}
            setDropGroup={setDropGroup}
            onDropOnGroup={handleDropOnGroup}
            onDropOnItem={handleDropOnItem}
            onHistory={setHistoryId}
            onAddChild={(parentId, parentName) => setGroupModal({ mode: 'create', parentId, parentName })}
            onRename={(group) => setGroupModal({ mode: 'rename', group })}
            onChanged={() => qc.invalidateQueries({ queryKey: ['wiki', 'standards'] })}
          />
        ))
      )}

      {canAdmin && (
        <div>
          <Button variant="ghost" icon={<PlusOutlined />} onClick={() => setGroupModal({ mode: 'create', parentId: null, parentName: null })}>
            Добавить блок
          </Button>
        </div>
      )}

      {canEdit && historyId && (
        <VersionHistory articleId={historyId} open={!!historyId} onClose={() => setHistoryId(null)} onRolledBack={() => undefined} />
      )}
      {groupModal && (
        <GroupModal modal={groupModal} onClose={() => setGroupModal(null)} onDone={() => qc.invalidateQueries({ queryKey: ['wiki', 'standards'] })} />
      )}
    </div>
  );
}

// ─── Дерево групп ─────────────────────────────────────────────────────

/** Строим дерево групп + раскладываем стандарты по группам; добавляем «Без блока». */
function buildTree(groups: StandardGroupDto[], items: StandardItemDto[]): GroupNode[] {
  const itemsByGroup = new Map<string, StandardItemDto[]>();
  for (const it of items) {
    const key = it.standardGroupId ?? UNGROUPED;
    const arr = itemsByGroup.get(key) ?? [];
    arr.push(it);
    itemsByGroup.set(key, arr);
  }
  // Сортировка стандартов внутри группы — по sortOrder, затем по дате обновления.
  for (const arr of itemsByGroup.values()) {
    arr.sort((a, b) => a.sortOrder - b.sortOrder || (a.updatedAt < b.updatedAt ? 1 : -1));
  }

  const byId = new Map<string, GroupNode>();
  for (const g of groups) byId.set(g.id, { group: g, children: [], items: itemsByGroup.get(g.id) ?? [] });

  const roots: GroupNode[] = [];
  for (const g of groups) {
    const node = byId.get(g.id)!;
    if (g.parentId && byId.has(g.parentId)) byId.get(g.parentId)!.children.push(node);
    else roots.push(node);
  }
  const sortNodes = (arr: GroupNode[]): void => {
    arr.sort((a, b) => (a.group?.sortOrder ?? 0) - (b.group?.sortOrder ?? 0));
    for (const n of arr) sortNodes(n.children);
  };
  sortNodes(roots);

  // «Без блока» — только если есть нераспределённые стандарты.
  const ungrouped = itemsByGroup.get(UNGROUPED) ?? [];
  if (ungrouped.length) {
    roots.push({ group: null, children: [], items: ungrouped });
  }
  return roots;
}

/** Рекурсивный подсчёт стандартов в узле + всех потомках (для счётчика блока). */
function countDeep(node: GroupNode): number {
  return node.items.length + node.children.reduce((s, c) => s + countDeep(c), 0);
}


interface BlockProps {
  node: GroupNode;
  depth: number;
  collapsed: Set<string>;
  onToggle: (id: string) => void;
  navigate: (to: string) => void;
  canAdmin: boolean;
  canEdit: boolean;
  dragId: string | null;
  dropGroup: string | null;
  setDragId: (id: string | null) => void;
  setDropGroup: (id: string | null) => void;
  onDropOnGroup: (groupId: string | null) => void;
  onDropOnItem: (target: StandardItemDto) => void;
  onHistory: (articleId: string) => void;
  onAddChild: (parentId: string, parentName: string) => void;
  onRename: (group: StandardGroupDto) => void;
  onChanged: () => void;
}

/** Группа-блок (панель с серой шапкой) или вложенная папка (depth>0). */
function GroupBlock(props: BlockProps): JSX.Element {
  const { node, depth, collapsed, onToggle, canAdmin, dropGroup, setDropGroup, onDropOnGroup } = props;
  const groupId = node.group?.id ?? UNGROUPED;
  const isOpen = !collapsed.has(groupId);
  const total = countDeep(node);
  const isDropTarget = dropGroup === groupId;
  const isRoot = depth === 0;
  const name = node.group?.name ?? 'Без блока';

  const dropProps = {
    onDragOver: (e: React.DragEvent) => {
      if (!canAdmin) return;
      e.preventDefault();
      setDropGroup(groupId);
    },
    onDragLeave: () => {
      if (dropGroup === groupId) setDropGroup(null);
    },
    onDrop: (e: React.DragEvent) => {
      if (!canAdmin) return;
      e.preventDefault();
      e.stopPropagation();
      onDropOnGroup(node.group?.id ?? null);
    },
  };

  const header = (
    <div
      className={isRoot ? 'fk-kb-std-group-head' : undefined}
      style={{
        ...(isRoot ? {} : { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px', borderBottom: '1px solid var(--fk-row-line)' }),
        cursor: 'pointer',
        background: isDropTarget ? 'var(--fk-orange-soft)' : undefined,
        outline: isDropTarget ? '2px dashed var(--fk-orange)' : 'none',
        outlineOffset: -2,
        paddingLeft: isRoot ? undefined : 18 + depth * 16,
      }}
      onClick={() => onToggle(groupId)}
      {...dropProps}
    >
      <span style={{ color: 'var(--fk-text-muted)', display: 'inline-flex', width: 12 }}>
        {isOpen ? <DownOutlined style={{ fontSize: 10 }} /> : <RightOutlined style={{ fontSize: 10 }} />}
      </span>
      <span style={{ fontWeight: 700, fontSize: isRoot ? 15 : 13.5 }}>{name}</span>
      <span style={{ fontSize: 12, color: 'var(--fk-text-muted)', flex: 1 }}>
        {total} {plural(total, 'стандарт', 'стандарта', 'стандартов')}
      </span>
      {canAdmin && node.group && <GroupMenu group={node.group} onAddChild={props.onAddChild} onRename={props.onRename} onChanged={props.onChanged} />}
    </div>
  );

  const body = isOpen && (
    <>
      {node.children.map((child) => (
        <GroupBlock key={child.group?.id ?? UNGROUPED} {...props} node={child} depth={depth + 1} />
      ))}
      {node.items.length === 0 && node.children.length === 0 ? (
        <div style={{ padding: '12px 18px', color: 'var(--fk-text-muted)', fontSize: 12.5, paddingLeft: 18 + depth * 16 }}>
          {canAdmin ? 'Пусто — перетащите сюда стандарт' : 'Нет стандартов'}
        </div>
      ) : (
        node.items.map((s) => <StandardRow key={s.id} {...props} item={s} />)
      )}
    </>
  );

  if (!isRoot) {
    return (
      <div>
        {header}
        {body}
      </div>
    );
  }
  return (
    <section className="fk-kb-panel" style={{ overflow: 'hidden' }}>
      {header}
      {body}
    </section>
  );
}

// ─── Строка стандарта ─────────────────────────────────────────────────

function StandardRow({
  item,
  depth,
  canAdmin,
  canEdit,
  navigate,
  dragId,
  setDragId,
  onDropOnItem,
  onHistory,
}: BlockProps & { item: StandardItemDto }): JSX.Element {
  const [over, setOver] = useState(false);
  const dragging = dragId === item.id;
  const st = STATUS_META[statusOf(item)];
  const pct = item.networkAckPct;
  return (
    <div
      className="fk-kb-std-row"
      onDragOver={(e) => {
        if (canAdmin && dragId && dragId !== item.id) {
          e.preventDefault();
          setOver(true);
        }
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        if (!canAdmin) return;
        e.preventDefault();
        e.stopPropagation();
        setOver(false);
        onDropOnItem(item);
      }}
      style={{
        paddingLeft: 18 + depth * 16,
        boxShadow: over ? 'inset 0 2px 0 var(--fk-orange)' : undefined,
        opacity: dragging ? 0.4 : 1,
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        {canAdmin && (
          <span
            draggable
            onDragStart={(e) => {
              e.stopPropagation();
              setDragId(item.id);
              e.dataTransfer.effectAllowed = 'move';
            }}
            onDragEnd={() => setDragId(null)}
            title="Перетащите, чтобы перенести или изменить порядок"
            className="fk-kb-grip"
          >
            <HolderOutlined />
          </span>
        )}
        <Link to={`/article/${item.slug}`} className="fk-kb-std-title">
          <SafetyCertificateOutlined style={{ color: 'var(--fk-orange)', flexShrink: 0 }} />
          <span>{item.title}</span>
        </Link>
      </span>
      <span className="fk-kb-mono fk-kb-std-ver" title={item.hasUpcoming ? `Сейчас действует ${item.currentEffectiveLabel}` : undefined}>
        {item.versionLabel}
      </span>
      <span className="fk-kb-std-date" style={{ color: 'var(--fk-text-2)' }}>
        {kbShortDate(item.updatedAt)}
      </span>
      <span className="fk-kb-std-status" style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
        <Tag color={st.color}>{st.label}</Tag>
        {item.hasUpcoming && item.upcomingEffectiveFrom && (
          <span style={{ fontSize: 11, color: 'var(--fk-blue)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <FieldTimeOutlined /> с {kbShortDate(item.upcomingEffectiveFrom)}
          </span>
        )}
      </span>
      <span style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
        {canAdmin ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }} title="Подтвердили по сети (пользователи CRM ролей стандарта)">
            <span className="fk-kb-bar" style={{ flex: 1 }}>
              <i style={{ width: `${pct ?? 0}%`, background: kbAckColor(pct) }} />
            </span>
            <span className="fk-kb-mono" style={{ minWidth: 34, textAlign: 'right', color: 'var(--fk-text)' }}>
              {pct == null ? '—' : `${pct}%`}
            </span>
          </span>
        ) : item.isAcked ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--fk-success-text)', fontWeight: 600 }}>
            <CheckCircleFilled /> Подтверждено
          </span>
        ) : (
          <Button size="sm" variant="primary" onClick={() => navigate(`/article/${item.slug}`)}>
            Подтвердить
          </Button>
        )}
        {canEdit && (
          <button type="button" className="fk-kb-icon-btn" title="История версий" onClick={() => onHistory(item.id)}>
            <HistoryOutlined />
          </button>
        )}
      </span>
    </div>
  );
}

// ─── ⋮-меню группы (Переименовать / + папку / Удалить) ─────────────────

function GroupMenu({
  group, onAddChild, onRename, onChanged,
}: {
  group: StandardGroupDto;
  onAddChild: (parentId: string, parentName: string) => void;
  onRename: (group: StandardGroupDto) => void;
  onChanged: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  // Координаты для портала меню (фикс относительно кнопки). Меню рендерится
  // в body, поэтому его НЕ обрезает overflow:hidden карточки-блока.
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const deleteMutation = useMutation({
    mutationFn: () => standardsApi.deleteGroup(group.id),
    onSuccess: () => { onChanged(); message.success('Блок удалён'); },
    onError: (e: Error) => message.error(e.message),
  });

  const toggle = (): void => {
    if (open) { setOpen(false); return; }
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
    setOpen(true);
  };

  return (
    <span onClick={(e) => e.stopPropagation()}>
      <button ref={btnRef} type="button" onClick={toggle} title="Действия с блоком" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--fk-text-muted)', padding: 2, display: 'inline-flex' }}>
        <MoreOutlined style={{ fontSize: 15 }} />
      </button>
      {open && pos && createPortal(
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1000 }} />
          <div style={{ position: 'fixed', top: pos.top, right: pos.right, width: 200, background: 'var(--fk-surface)', border: '1px solid var(--fk-border)', borderRadius: 'var(--fk-radius-md)', boxShadow: 'var(--fk-shadow-pop)', zIndex: 1001, padding: 6 }}>
            <MenuItem icon={<EditOutlined />} label="Переименовать" onClick={() => { onRename(group); setOpen(false); }} />
            <MenuItem icon={<FolderAddOutlined />} label="Добавить подблок" onClick={() => { onAddChild(group.id, group.name); setOpen(false); }} />
            <div style={{ borderTop: '1px solid var(--fk-border)', margin: '4px 0' }} />
            <Popconfirm
              title="Удалить блок?"
              description="Удалить можно только пустой блок без подпапок и стандартов"
              okText="Удалить"
              cancelText="Отмена"
              okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
              onConfirm={() => { setOpen(false); deleteMutation.mutate(); }}
            >
              <MenuItem icon={<DeleteOutlined />} label="Удалить блок" danger />
            </Popconfirm>
          </div>
        </>,
        document.body,
      )}
    </span>
  );
}

function MenuItem({ icon, label, onClick, danger }: { icon: JSX.Element; label: string; onClick?: () => void; danger?: boolean }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', borderRadius: 4, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: danger ? 'var(--fk-error)' : 'var(--fk-text)', textAlign: 'left', fontFamily: 'inherit' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--fk-bg-subtle)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{ display: 'inline-flex' }}>{icon}</span>{label}
    </button>
  );
}

// ─── Модалка создания / переименования блока ──────────────────────────

type GroupModalState =
  | { mode: 'create'; parentId: string | null; parentName: string | null }
  | { mode: 'rename'; group: StandardGroupDto };

function GroupModal({ modal, onClose, onDone }: { modal: GroupModalState; onClose: () => void; onDone: () => void }): JSX.Element {
  const [name, setName] = useState(modal.mode === 'rename' ? modal.group.name : '');
  const isRename = modal.mode === 'rename';
  const title = isRename
    ? 'Переименовать блок'
    : modal.parentId
      ? `Новая папка в «${modal.parentName ?? ''}»`
      : 'Новый блок стандартов';

  const saveMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      const n = name.trim();
      if (modal.mode === 'rename') await standardsApi.renameGroup(modal.group.id, n);
      else await standardsApi.createGroup({ name: n, parentId: modal.parentId });
    },
    onSuccess: () => { onDone(); message.success(isRename ? 'Переименовано' : 'Создано'); onClose(); },
    onError: (e: Error) => message.error(e.message),
  });

  return (
    <Modal
      open
      title={title}
      onCancel={onClose}
      onOk={() => saveMutation.mutate()}
      okText={isRename ? 'Сохранить' : 'Создать'}
      cancelText="Отмена"
      confirmLoading={saveMutation.isPending}
      okButtonProps={{ disabled: !name.trim() }}
    >
      <Input
        autoFocus
        placeholder="Название"
        value={name}
        maxLength={120}
        onChange={(e) => setName(e.target.value)}
        onPressEnter={() => { if (name.trim()) saveMutation.mutate(); }}
      />
    </Modal>
  );
}
